import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileSearch, Loader2, Upload } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const CHUNK = 4 * 1024 * 1024;
const REL = { coincide: ['Coincide con tu doctrina', 'text-emerald-300 border-emerald-500/40'], se_aparta: ['Se aparta de tu doctrina', 'text-rose-300 border-rose-500/40'], matiza: ['Matiza tu doctrina', 'text-amber-300 border-amber-500/40'], no_relacionado: ['No trata tu doctrina', 'text-slate-400 border-slate-600'] };

const CheckCard = ({ c }) => (
  <div data-testid={`ruling-check-${c.id}`} className={`rounded-xl border bg-slate-950/40 p-3 text-xs ${REL[c.relacion]?.[1] || 'border-slate-700'}`}>
    <p className="font-semibold">{REL[c.relacion]?.[0] || c.relacion} · <span className="text-white">{c.tribunal || 'Tribunal s/i'} · {c.rol}{c.fecha ? ` · ${c.fecha}` : ''}</span></p>
    {c.sintesis && <p className="mt-1 text-slate-300">{c.sintesis}</p>}
    {(c.por_autor || []).map((x, i) => <p key={i} className="mt-1 text-slate-400"><b>{x.autor}</b>{x.obra ? `, «${x.obra}»` : ''}{x.pagina ? `, p. ${x.pagina}` : ''} · {x.relacion?.replace('_', ' ')}: {x.explicacion}</p>)}
    {c.impacto && <p className="mt-1 text-slate-300"><b>En tu causa:</b> {c.impacto}</p>}
    {c.como_usarlo && <p className="mt-1 text-slate-400"><b>Cómo usarlo:</b> {c.como_usarlo}</p>}
  </div>
);

// Fallo en la Causa: upload or paste a ruling and Fidem compares it at once with this matter's doctrine
export const MatterRulingCheck = ({ matter, onUpdated }) => {
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const ref = useRef(null);
  if ((matter.country && matter.country !== 'chile') || matter.user_id !== user?.id) return null;
  const checks = [...(matter.ruling_checks || [])].reverse();
  const finish = (r) => { setResult(r); setText(''); onUpdated(); toast.success('Fidem comparó el fallo con tu doctrina'); };
  const fail = (e) => toast.error(e.response?.data?.detail || e.message || 'No se pudo comparar');
  const upload = async (file) => {
    if (!file) return;
    const id = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    setBusy('Subiendo...');
    try {
      for (let i = 0; i < total; i += 1) await api.post(`/doctrine/upload/${id}/chunk`, file.slice(i * CHUNK, (i + 1) * CHUNK), { params: { index: i }, headers: { 'Content-Type': 'application/octet-stream' } });
      setBusy('Fidem compara el fallo...');
      finish(await runAiJob(api, `/doctrine/matters/${matter.id}/ruling/upload/${id}/finish`, { filename: file.name, total }, { timeout: 300000 }));
    } catch (e) { fail(e); } finally { setBusy(null); if (ref.current) ref.current.value = ''; }
  };
  const paste = async () => {
    setBusy('Fidem compara el fallo...');
    try { finish(await runAiJob(api, `/doctrine/matters/${matter.id}/ruling/text`, { text }, { timeout: 300000 })); } catch (e) { fail(e); } finally { setBusy(null); }
  };
  return (
    <>
      <button data-testid={`matter-ruling-check-btn-${matter.id}`} onClick={() => { setResult(null); setOpen(true); }} className="flex items-center gap-1.5 rounded-full border border-amber-500/40 px-3 py-1 text-xs text-amber-200 hover:bg-amber-500/10">
        <FileSearch className="h-3 w-3" />Fallo en la causa{checks.length ? ` · ${checks.length}` : ''}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="matter-ruling-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-700 bg-slate-950 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Fallo en la causa</DialogTitle>
            <DialogDescription className="text-slate-400">Sube un fallo (PDF o Word) o pega su texto: Fidem lo compara al instante con la doctrina de «{matter.title}».</DialogDescription>
          </DialogHeader>
          <input ref={ref} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="matter-ruling-file" onChange={(e) => upload(e.target.files[0])} />
          <button data-testid="matter-ruling-upload-btn" disabled={!!busy} onClick={() => ref.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 py-2 text-sm font-semibold text-slate-950 disabled:opacity-60">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{busy}</> : <><Upload className="h-4 w-4" />Subir fallo (PDF, Word o texto)</>}
          </button>
          <textarea data-testid="matter-ruling-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="...o pega aquí el texto del fallo (mínimo 800 caracteres)" className="w-full rounded-lg border border-slate-700 bg-slate-900 p-3 text-xs text-slate-200" />
          <button data-testid="matter-ruling-paste-btn" disabled={!!busy || text.trim().length < 800} onClick={paste} className="rounded-lg border border-amber-500/60 py-2 text-sm text-amber-200 disabled:opacity-40">Comparar texto pegado{text && text.trim().length < 800 ? ` (${text.trim().length}/800)` : ''}</button>
          {result && <div data-testid="matter-ruling-result"><CheckCard c={result} /></div>}
          {checks.length > 0 && <p className="mt-2 text-xs font-semibold text-slate-400">Fallos comparados en esta causa</p>}
          <div className="space-y-2">{checks.filter((c) => c.id !== result?.id).map((c) => <CheckCard key={c.id} c={c} />)}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};
